import styled from 'styled-components';

// Container — shell unico estilo X/Twitter (igual /reports e /dashboard).
// height: 100vh + overflow hidden trava a pagina inteira na altura da
// viewport — sem isso, sidebar com muitas aplicacoes empurrava o mapa
// pra ficar gigante. Agora mapa fica fixo na area restante do
// FormColumn e SidebarBody scrollla independente.
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100vw - 64px);
  height: 100vh;
  margin: 0 0 0 64px;
  padding: 0;
  box-sizing: border-box;
  background: var(--cco-bg);
  color: var(--cco-ink);
  overflow: hidden;

  /* abaixo de 1100px o grid colapsa pra 1 col — permite scroll da
     pagina inteira ja que mapa + sidebar ficam empilhados e somam
     mais que a viewport. */
  @media (max-width: 1100px) {
    height: auto;
    min-height: 100vh;
    overflow: visible;
  }

  /* Mobile: sidemenu colapsa, container ocupa tela inteira. */
  @media (max-width: 720px) {
    width: 100vw;
    margin: 0;
  }
`;

// HeaderBar — 2 colunas LADO A LADO separadas por hairline vertical:
//   esquerda: HeaderBreadcrumbRow (Dashboard › Cliente)
//   | hairline vertical |
//   direita:  HeaderInfoRow (📍 SABESP + status chip)
// Border-bottom hairline tambem separa do conteudo abaixo (mapa+sidebar).
// Em viewport <1100px vira coluna unica com hairlines horizontais.
export const HeaderBar = styled.section`
  display: flex;
  align-items: stretch;
  width: 100%;
  box-sizing: border-box;
  border-bottom: 1px solid var(--cco-line);

  /* hairline vertical entre as duas regioes via combinator > * + * */
  > * + * {
    border-left: 1px solid var(--cco-line);
  }

  @media (max-width: 1100px) {
    flex-direction: column;
    > * + * {
      border-left: 0;
      border-top: 1px solid var(--cco-line);
    }
  }
`;

// Esquerda: breadcrumb.
export const HeaderBreadcrumbRow = styled.div`
  display: flex;
  align-items: center;
  padding: 10px var(--cco-pad);
  min-height: 56px;
  flex: 0 0 auto;
`;

// Direita: pin + nome + status chip lado a lado. flex:1 pra ocupar
// o resto do espaco e flex-wrap pra acomodar viewports estreitos.
export const HeaderInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 10px var(--cco-pad);
  min-height: 56px;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 10px;
    min-height: 48px;
  }
`;

// HeaderBreadcrumb — pass-through pra Breadcumb.
export const HeaderBreadcrumb = styled.div`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  min-width: 0;
`;

// HeaderName — pin + nome. Agora inline (nao mais absolute centrado),
// ja que vive em uma row propria com gap pro status.
export const HeaderName = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--cco-ink);
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// HeaderLeft kept como compat-export caso algum lugar ainda referencie.
export const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

// HeaderPin — circulo blue-soft com icone de pin (lucide:map-pin).
export const HeaderPin = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--cco-blue-soft);
  color: var(--cco-blue);
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`;

// HeaderRight — kept como compat. Hoje o status chip vive direto na
// HeaderInfoRow ao lado do HeaderName.
export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

// StatusChip — pill com dot pulsante + label. Cor vem inline por
// prop (verde rodando / vermelho alarme / laranja warning / cinza off).
export const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px 6px 10px;
  border-radius: 999px;
  border: 1px solid ${(props) => props.$color || 'var(--cco-line)'};
  background: ${(props) =>
    props.$color ? `${props.$color}22` : 'transparent'};
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${(props) => props.$color || 'var(--cco-ink-mute)'};
  white-space: nowrap;
`;

export const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => props.$color || 'var(--cco-ink-mute)'};
  box-shadow: 0 0 0 3px
    ${(props) => (props.$color ? `${props.$color}33` : 'transparent')};
`;

// MainGrid — corpo principal grid 2-col: form (1fr) | sidebar (360px).
// Border-left hairline entre eles continua a divisao do HeaderBar.
// flex: 1 1 0 + min-height 0 + overflow hidden trava no espaco
// vertical sobrando depois do HeaderBar (sem isso, conteudo do
// SidebarBody empurrava a altura).
export const MainGrid = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  width: 100%;
  box-sizing: border-box;
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;

  > * + * {
    border-left: 1px solid var(--cco-line);
  }

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    overflow: visible;
    > * + * {
      border-left: none;
      border-top: 1px solid var(--cco-line);
    }
  }
`;

export const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  min-height: 0;
  overflow: hidden;

  > * + * {
    border-top: 1px solid var(--cco-line);
  }
`;

export const SidebarColumn = styled.aside`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

// MapSection — mapa flush (sem padding pra ocupar largura inteira).
// min-height pra garantir que mesmo em tela pequena o mapa tenha
// altura util.
export const MapSection = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  min-height: 360px;
  flex: 1 1 auto;
  overflow: hidden;

  /* o ZonasClientesMapa renderiza um div absolute internamente
     em modo embedded — garantir que a section sirva de containing
     block (position relative ja faz isso). */
  > div {
    flex: 1;
    min-height: 0;
  }

  /* Mobile: mapa ocupa altura natural mas com piso adequado. */
  @media (max-width: 720px) {
    min-height: 280px;
  }
`;

// SummarySection — chip "TOTAL DE N EBAS/UCHAS" + descricao inline,
// padding lateral pra alinhar com SectionHead da pag /reports.
export const SummarySection = styled.section`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
  padding: 14px var(--cco-pad);
`;

export const SummaryChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  background: var(--cco-blue-soft);
  color: var(--cco-blue);
  box-shadow: inset 0 0 0 1px rgba(78, 161, 243, 0.25);
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  flex-shrink: 0;
`;

export const SummaryText = styled.p`
  margin: 0;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--cco-ink-mute);
  line-height: 1.5;
  flex: 1;
  min-width: 200px;
`;

export const SummaryAccent = styled.span`
  color: var(--cco-ink);
  font-weight: 700;
`;

// MetricsSection — abaixo do summary. O VisaoGeralUchasEbas ja
// renderiza donut + AGORA + ENERGIA com seu proprio padding interno
// (TagTitle padding: 18px var(--cco-pad)) — entao aqui NAO adicionamos
// padding lateral, so vertical, pra nao duplicar com o interno.
// Barra de escopo da selecao — aparece acima das metricas quando um
// parque esta selecionado. Informa O QUE os numeros do AGORA refletem e
// oferece "Ver todos" pra desselecionar (volta aos numeros do cliente).
export const ScopeBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 var(--cco-pad) 8px;
  padding: 8px 12px;
  border: 1px solid var(--cco-line);
  border-radius: 10px;
  background: var(--cco-blue-soft);

  > .lbl {
    font-size: 12px;
    color: var(--cco-ink-mute);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    strong {
      color: var(--cco-blue);
      font-weight: 700;
      margin-left: 4px;
    }
  }

  > button {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;
    padding: 5px 10px;
    border-radius: 8px;
    border: 1px solid var(--cco-blue);
    background: transparent;
    color: var(--cco-blue);
    font-family: inherit;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: background 150ms ease, color 150ms ease;

    svg {
      width: 13px;
      height: 13px;
    }

    :hover {
      background: var(--cco-blue);
      color: #fff;
    }
  }
`;

export const MetricsSection = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  padding: 0 0 8px;
  min-height: 0;
  flex: 0 1 auto;

  /* segundo card de uchas/ebas — quando ambos presentes, lado a lado.
     Hairline vertical entre os dois imita a divisao do MainGrid. */
  &[data-pair='true'] {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 0;
    align-items: start;
    > * + * {
      border-left: 1px solid var(--cco-line);
    }
  }
`;

// SidebarHeader — "APLICAÇÕES" label + botao "+ CADASTRAR" no topo
// do SidebarColumn. Border-bottom hairline divide do conteudo.
export const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px var(--cco-pad);
  border-bottom: 1px solid var(--cco-line);
  flex-shrink: 0;
`;

export const SidebarTitle = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--cco-ink-mute);
`;

// CadastrarButton — outline azul discreto com icone +.
export const CadastrarButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid var(--cco-blue);
  background: transparent;
  color: var(--cco-blue);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  font-family: inherit;
  transition: background 150ms ease;

  svg {
    width: 13px;
    height: 13px;
  }

  :hover {
    background: var(--cco-blue-soft);
  }
`;

// SidebarBody — area scrollavel com a grid de aplicacoes.
export const SidebarBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 14px var(--cco-pad);
`;

// SidebarFooter — contagem total no rodape ("1 APLICAÇÃO CADASTRADA").
// Hairline em cima separa da grid.
export const SidebarFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 12px var(--cco-pad);
  border-top: 1px solid var(--cco-line);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--cco-ink-mute);
  flex-shrink: 0;
`;

// Skeleton: mantido (usado durante loading) — shimmer reactivo ao tema.
const shimmer = `
  @keyframes shimmer-cliente {
    0% { background-position: -800px 0; }
    100% { background-position: 800px 0; }
  }
`;

export const SkeletonBlock = styled.div`
  ${shimmer}
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'linear-gradient(90deg, rgba(0,0,0,0.04) 0px, rgba(0,0,0,0.08) 200px, rgba(0,0,0,0.04) 400px)'
      : 'linear-gradient(90deg, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.08) 200px, rgba(255,255,255,0.04) 400px)'};
  background-size: 800px 100%;
  animation: shimmer-cliente 1.4s linear infinite;
  border-radius: 8px;
`;

// Compat — alguns lugares (skeleton, etc) ainda referenciam estes nomes.
// Mantidos como aliases simples pra nao quebrar imports antigos.
export const Wrapper = styled.div`
  display: flex;
  width: 100%;
`;
export const Card = styled.div`
  display: flex;
  width: 100%;
  box-sizing: border-box;
`;
export const Line = styled.div`
  display: flex;
  width: 100%;
  gap: 16px;
`;
export const MapWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
export const TwoColumns = styled.div`
  display: flex;
  width: 100%;
`;
export const ColumnStack = styled.div`
  display: flex;
  flex-direction: column;
  flex: ${(props) => props.flex || 1};
`;
